import * as React from 'react';
import { Map, MapCameraChangedEvent } from '@vis.gl/react-google-maps';

// Utils
import { cond, gte, T, always, partial } from '@src/shared/src/util/general';
import { getHotelsResultsByRating } from '@toolkit/util/app';
// Constants
import { GOOGLE_MAPS_MAP_IDS } from '@toolkit/const/app';
// Actions, Models & Interfaces
import { HotelModel, CoordinatesModel } from '@src/shared/src/models';
// Components
import HotelMapMarker from './HotelMapMarker';
import HotelDestMarker from './HotelDestMarker';
// Styles
import '../styles/HotelMap.scss';

type Props = {
  handleViewOffer: (hotelId: string) => void;
  showHotelsList: () => void;
  hotels: HotelModel[];
  center: CoordinatesModel;
  location: CoordinatesModel;
};

const HotelsMap: React.FC<Props> = ({
  handleViewOffer,
  showHotelsList,
  hotels,
  center,
  location,
}) => {
  const [filteredHotels, setFilteredHotels] = React.useState<HotelModel[]>([]);
  const [displayHotelTooltip, setDisplayHotelTooltip] = React.useState<string | null>(null);
  const [currentZoom, setCurrentZoom] = React.useState<number>(12);

  const filterHotelsbyRatingAndZoomLevel = (hotels: HotelModel[]) => {
    const getHotelsByZoom = cond([
      [gte(8), partial(getHotelsResultsByRating, [hotels, 10])],
      [gte(9), partial(getHotelsResultsByRating, [hotels, 9])],
      [gte(10), partial(getHotelsResultsByRating, [hotels, 8])],
      [gte(11), partial(getHotelsResultsByRating, [hotels, 7])],
      [gte(12), partial(getHotelsResultsByRating, [hotels, 6])],
      [gte(13), partial(getHotelsResultsByRating, [hotels, 5])],
      [gte(14), partial(getHotelsResultsByRating, [hotels, 4])],
      [gte(15), partial(getHotelsResultsByRating, [hotels, 3])],
      [T, always(hotels)],
    ]);
    return getHotelsByZoom(currentZoom);
  };

  React.useEffect(() => {
    setFilteredHotels(filterHotelsbyRatingAndZoomLevel(hotels));
  }, [hotels, currentZoom]);

  const handleZoomChanged = (event: MapCameraChangedEvent) => {
    const newZoom = event.map.getZoom();
    if (newZoom !== undefined) {
      setCurrentZoom(newZoom);
      setFilteredHotels(filterHotelsbyRatingAndZoomLevel(hotels));
    }
  };

  const handleViewOfferClick = (hotelId: string) => {
    handleViewOffer(hotelId);
    showHotelsList();
  };

  const handleTooltip = (hotelId: string) => {
    setDisplayHotelTooltip((prev) => (prev === hotelId ? null : hotelId));
  };

  return (
    <div className="map-container">
      <div className="map-element">
        <Map
          defaultCenter={center}
          defaultZoom={12}
          mapId={GOOGLE_MAPS_MAP_IDS.HOTELS_MAP_ID}
          minZoom={8}
          maxZoom={17}
          fullscreenControl={false}
          mapTypeControl={false}
          streetViewControl={false}
          onZoomChanged={handleZoomChanged}>
          {filteredHotels.map((hotel) => (
            <HotelMapMarker
              key={hotel.id}
              hotel={hotel}
              onShowDetails={handleViewOfferClick}
              onHandelTooltip={handleTooltip}
              isTooltipOpen={displayHotelTooltip === hotel.id}
            />
          ))}
          <HotelDestMarker position={location} />
        </Map>
      </div>
    </div>
  );
};

export default HotelsMap;
